import type { FC, ReactNode } from 'react';
import React, { createContext, useEffect, useRef, useState } from 'react';
import type { Card } from '@adyen/adyen-web';
import { OrderingSelectors } from 'polygon-ordering';
import usePaymentStatusPoller from '../../hooks/usePaymentStatusPoller';
import { PAYMENT_SESSION_STATUS } from '../../libs/polygon-ordering/src/constants/paymentSessionStatus';
import { useAppSelector } from '../../app/hooks';
import type { PaymentPurchaseType } from '../../analytics';
import {
  trackPaymentFailed,
  trackPaymentInitiated,
  trackPaymentRetry,
  trackPaymentSucceeded,
  trackPaymentTimeout,
  useRedcatAnalytics,
} from '../../analytics';

const { getMember } = OrderingSelectors;

export type ResetPaymentAttemptOptions = {
  retainIdempotencyKey?: boolean;
  retainPollingReference?: boolean;
};

export type RedcatPaymentStore = {
  registerCardComponent: (card: InstanceType<typeof Card>) => void;
  unregisterCardComponent: () => void;
  submitRedcatPay: () => boolean;
  cardReady: boolean;
  cardValid: boolean;
  setCardValid: (valid: boolean) => void;
  pollingReference: string | null;
  setPollingReference: (reference: string | null) => void;
  idempotencyKey: string | null;
  setIdempotencyKey: (key: string | null) => void;
  paymentStatus: PAYMENT_SESSION_STATUS | null;
  paymentComplete: boolean;
  paymentTimedOut: boolean;
  resumePaymentStatusPolling: () => void;
  paymentInProgress: boolean;
  setPaymentInProgress: (inProgress: boolean) => void;
  recaptchaToken: string | null;
  setRecaptchaToken: (token: string | null) => void;
  resetPaymentAttempt: (options?: ResetPaymentAttemptOptions) => void;
  requireRecaptcha: boolean;
  recaptchaSiteKey: string | null;
  purchaseType?: PaymentPurchaseType;
};

export const RedcatPaymentContext = createContext<RedcatPaymentStore>({
  registerCardComponent: () => {},
  unregisterCardComponent: () => {},
  submitRedcatPay: () => false,
  cardReady: false,
  cardValid: false,
  setCardValid: () => {},
  pollingReference: null,
  setPollingReference: () => {},
  idempotencyKey: null,
  setIdempotencyKey: () => {},
  paymentStatus: null,
  paymentComplete: false,
  paymentTimedOut: false,
  resumePaymentStatusPolling: () => {},
  paymentInProgress: false,
  setPaymentInProgress: () => {},
  recaptchaToken: null,
  setRecaptchaToken: () => {},
  resetPaymentAttempt: () => {},
  requireRecaptcha: false,
  recaptchaSiteKey: null,
});

interface RedcatPaymentProviderProps {
  readonly children: ReactNode;
  readonly requireRecaptcha?: boolean;
  readonly recaptchaSiteKey?: string | null;
  readonly purchaseType?: PaymentPurchaseType;
}

export const RedcatPaymentProvider: FC<RedcatPaymentProviderProps> = ({
  children,
  requireRecaptcha = false,
  recaptchaSiteKey = null,
  purchaseType,
}) => {
  const cardComponentRef = useRef<InstanceType<typeof Card> | null>(null);
  const [cardReady, setCardReady] = useState(false);
  const [cardValid, setCardValid] = useState(false);
  const [pollingReference, setPollingReference] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const {
    status: paymentStatus,
    isComplete: paymentComplete,
    timedOut: paymentTimedOut,
    resumePolling: resumePaymentStatusPolling,
  } = usePaymentStatusPoller(pollingReference);

  const { track } = useRedcatAnalytics();
  const member = useAppSelector(getMember);
  const analyticsBase = {
    purchaseType,
    memberId: member?.id,
    transactionId: pollingReference,
  };

  useEffect(() => {
    if (paymentStatus === PAYMENT_SESSION_STATUS.PAYMENT_SUCCESS) {
      trackPaymentSucceeded(track, analyticsBase);
    } else if (paymentStatus === PAYMENT_SESSION_STATUS.PAYMENT_FAILED) {
      trackPaymentFailed(track, { ...analyticsBase, failureReason: 'payment_session_failed' });
    }
  }, [paymentStatus]);

  useEffect(() => {
    if (paymentTimedOut) trackPaymentTimeout(track, analyticsBase);
  }, [paymentTimedOut]);

  const trackedResumePaymentStatusPolling = () => {
    trackPaymentRetry(track, analyticsBase);
    resumePaymentStatusPolling();
  };

  const resetPaymentAttempt = (options?: ResetPaymentAttemptOptions) => {
    setRecaptchaToken(null);
    setPaymentInProgress(false);
    if (!options?.retainPollingReference) setPollingReference(null);
    if (!options?.retainIdempotencyKey) setIdempotencyKey(null);
  };

  const registerCardComponent = (card: InstanceType<typeof Card>) => {
    cardComponentRef.current = card;
    setCardReady(true);
  };

  const unregisterCardComponent = () => {
    cardComponentRef.current = null;
    setCardReady(false);
  };

  const submitRedcatPay = (): boolean => {
    const card = cardComponentRef.current;
    if (!card || !card.isValid) return false;

    // No reference yet on the new-card path — Adyen only returns it via onPaymentCompleted.
    trackPaymentInitiated(track, analyticsBase);
    card.submit();
    return true;
  };

  return (
    <RedcatPaymentContext.Provider
      value={{
        registerCardComponent,
        unregisterCardComponent,
        submitRedcatPay,
        cardReady,
        cardValid,
        setCardValid,
        pollingReference,
        setPollingReference,
        idempotencyKey,
        setIdempotencyKey,
        paymentStatus,
        paymentComplete,
        paymentTimedOut,
        resumePaymentStatusPolling: trackedResumePaymentStatusPolling,
        paymentInProgress,
        setPaymentInProgress,
        recaptchaToken,
        setRecaptchaToken,
        resetPaymentAttempt,
        requireRecaptcha,
        recaptchaSiteKey,
        purchaseType,
      }}
    >
      {children}
    </RedcatPaymentContext.Provider>
  );
};
